import { useTranslation } from "react-i18next";
import { EmptyState, Notify } from "components/core";
import { ListStatus } from "molecules/listState/useListStatus";
import { ListCard } from "../../core/cards/ListCard";
import styles from "./ListViewLayout.module.css";
import moment from "moment";
import {
  useUserService,
  useVisualizationService,
  useDashboardService,
  useActionsService,
} from "services";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  currentRoleState,
  Role,
  rolesState,
  usersForCurrentRoleState,
  newItemState,
  editItemState,
  User,
  usersState,
  currentUserState,
  visualizationsState,
  currentVisualizationState,
  Visualization,
  currentDashboardState,
  dashboardsState,
  Dashboard,
  filteredSearchList,
  Actions,
  PermissionsList,
  VisualFilteredSearchList,
  updateItemState,
} from "states";
import { useLocation } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import "styles/global.css";
import { DASHBOARD_PROFILE, ENTITY_TYPE } from "../../../constants";
import { getVizTypeConfig } from "../../../constants/vizTypeConfig";
import { matchesListSearch, SearchableListItem } from "helpers";
import { activateProps } from "helpers/a11y";
import { getDashboardTag, DASHBOARD_TAG_I18N } from "helpers/dashboardStatus";
import { StatusBadge } from "atoms/statusBadge/StatusBadge";
import { NO_FILTERS, UserFilterValue, hasFilters, matchesUserFilters } from "helpers/userFilters";
import { UserFilters } from "molecules/userList/UserFilters";

interface ListViewLayoutProps {
  listItems: any;
  showFloatingBtn: boolean;
  btnText?: string;
  itemType: string;
  /** Whether the list is still loading, loaded, or failed to load. Defaults to loaded. */
  status?: ListStatus;
  onRetry?: () => void;
}

// Accent + icon-background pairs used to visually differentiate cards in the grid.
// iconBg intentionally uses the neutral surface token (not a colored tint) so it
// stays correct across every theme without any hardcoded/new colors — the
// per-item differentiation comes from iconColor/accent (--chart-N tokens).
const CARD_ACCENTS = [
  { accent: "var(--chart-1)", iconBg: "var(--bg-surface-secondary)", iconColor: "var(--chart-1)" },
  { accent: "var(--chart-3)", iconBg: "var(--bg-surface-secondary)", iconColor: "var(--chart-3)" },
  { accent: "var(--chart-4)", iconBg: "var(--bg-surface-secondary)", iconColor: "var(--chart-4)" },
  { accent: "var(--chart-6)", iconBg: "var(--bg-surface-secondary)", iconColor: "var(--chart-6)" },
  { accent: "var(--chart-5)", iconBg: "var(--bg-surface-secondary)", iconColor: "var(--chart-5)" },
  { accent: "var(--chart-7)", iconBg: "var(--bg-surface-secondary)", iconColor: "var(--chart-7)" },
];

interface CreateItemOptions {
  setIsNewItem: (value: boolean) => void;
  setEditItem: (value: boolean) => void;
  itemType: string;
  addNewVisualization: () => void;
  addNewDashboard: () => void;
  addNewUser: () => void;
  addNewRole: () => void;
}

const createItem = ({
  setIsNewItem,
  setEditItem,
  itemType,
  addNewVisualization,
  addNewDashboard,
  addNewUser,
  addNewRole,
}: CreateItemOptions) => (e?: { preventDefault: () => void }) => {
  e?.preventDefault();
  // Force reset first in case state is stale from previous navigation
  setIsNewItem(false);
  setIsNewItem(true); setEditItem(false);
  switch (itemType) {
    case ENTITY_TYPE.VISUALIZATION: addNewVisualization(); break;
    case ENTITY_TYPE.DASHBOARD:     addNewDashboard();     break;
    case ENTITY_TYPE.USER:          addNewUser();          break;
    case ENTITY_TYPE.ROLE:          addNewRole();          break;
    default: return null;
  }
};

export const ListViewLayout = ({ listItems, showFloatingBtn, btnText, itemType, status = 'ready', onRetry }: ListViewLayoutProps) => {
  const { t } = useTranslation();
  const [customListItems, setCustomListItems] = useState<any[]>([]);
  const [searchQuery, setSearchQuery]         = useState("");
  const [userFilters, setUserFilters] = useState<UserFilterValue>(NO_FILTERS);
  const [userViewMode,    setUserViewMode]    = useState<"list"|"tile">("list");
  const [vizViewMode,     setVizViewMode]     = useState<"list"|"tile">("list");

  const [, setIsNewItem]                    = useRecoilState(newItemState);
  const [updatedList, setUpdatedList]       = useRecoilState(updateItemState);
  const setEditItem                          = useSetRecoilState(editItemState);
  const userService                          = useUserService();
  const [users, setUsers]                   = useRecoilState(usersState);
  const [currentUser, setCurrentUser]       = useRecoilState(currentUserState);
  const [currentRole, setCurrentRole]       = useRecoilState(currentRoleState);
  const [roles, setRoles]                   = useRecoilState(rolesState);
  const [visualizations, setVisualizations] = useRecoilState(visualizationsState);
  const [currentVisualization, setCurrentVisualization] = useRecoilState(currentVisualizationState);
  const setUsersForCurrentRole               = useSetRecoilState(usersForCurrentRoleState);
  const [currentDashboard, setCurrentDashboard] = useRecoilState(currentDashboardState);
  const [dashboards, setDashboards]         = useRecoilState(dashboardsState);
  const visualizationService                 = useVisualizationService();
  const dashboardService                     = useDashboardService();
  const actionService                        = useActionsService();
  const [, setActions]                      = useRecoilState(Actions);
  const [, setPermissionsList]              = useRecoilState(PermissionsList);
  const [visualisationFilteredList, setVisualisationFilteredList] = useRecoilState(VisualFilteredSearchList);
  const [activeTypeFilter, setActiveTypeFilter] = useState("all");

  const chartTypes = useMemo(() => {
    const types = new Set<string>();
    visualisationFilteredList.forEach((v: Visualization) => {
      const ct = (v.data?.[0]?.info?.chartType || "").toLowerCase();
      if (ct) types.add(ct);
    });
    return Array.from(types);
  }, [visualisationFilteredList]);

  const [filteredList, setFilteredList] = useRecoilState(filteredSearchList);
  const location                         = useLocation();

  useEffect(() => {
    setCustomListItems(listItems);
    setFilteredList(listItems);
    setVisualisationFilteredList(listItems);
    setUpdatedList(listItems);
  }, [listItems]);

  const displayedDashboards = searchQuery.trim()
    ? filteredList.filter((d: Dashboard) =>
        (d.title || "").toLowerCase().includes(searchQuery.toLowerCase())
      )
    : filteredList;

  const selectItem = (item: any) => {
    switch (itemType) {
      case ENTITY_TYPE.DASHBOARD:     selectDashboard(item);    break;
      case ENTITY_TYPE.VISUALIZATION: selectVisualization(item); break;
      case ENTITY_TYPE.USER:          selectUser(item.id);       break;
      case ENTITY_TYPE.ROLE:          selectRole(item.id);       break;
      default: return null;
    }
  };

  const matchPathSelection = (type: string, path: string, id: any) => {
    if (type === "dashboard"     && path === "dashboards"     && id === currentDashboard.id)    return true;
    if (type === "visualization" && path === "visualizations" && id === currentVisualization.id) return true;
    if (type === "user"          && path === "users"          && id === currentUser.id)          return true;
    if (type === "role"          && path === "roles"          && id === currentRole.id)          return true;
    return false;
  };

  const addNewRole = () => {
    const n = { name: "New Role", description: "role description" };
    setCurrentRole(n); setUsersForCurrentRole([]); setRoles((o) => [...o, n]);
  };
  const addNewUser = () => {
    const n = { firstName: "New User", emailId: "", phoneNo: "", username: "" };
    setCurrentUser(n); setUsers((o) => [...o, n]);
  };
  const addNewVisualization = () => {
    const n: Visualization = {
      visualizationCode: "newVisualization", title: "New Visualization",
      isPinned: false, isDecimal: false, type: "visualization", visualizationPermission: [],
      data: [{ info: { title: "New Visualization", chartType: "", subTitle: "" },
               data: { query: "" }, access: { escapedAccessConfig: [], accessConfig: "" },
               additionalConfig: { config: "" } }],
    };
    setCurrentVisualization(n); setVisualizations((o) => [...o, n]);
  };
  const addNewDashboard = () => {
    const n: Dashboard = {
      title: "New Dashboard", isPinned: false, type: "dashboard",
      dashboardId: "newDashboard", profile: DASHBOARD_PROFILE,
      data: [{ info: { title: "New Dashboard", description: "" },
               layout: { layoutConfig: "" }, filters: { visualConfig: "", showDateFilter: false },
               config: { dashboardConfig: "" }, widgets: { widgetsConfig: "" },
               access: { escapedAccessConfig: [], accessConfig: "" } }],
    };
    setCurrentDashboard(n); setDashboards((o) => [...o, n]);
  };

  const selectRole = (id: number) => {
    if (!id || id === currentRole.id) return false;
    setEditItem(false);
    const ui = roles.findIndex((r: Role) => r.id === undefined);
    if (ui !== -1) { const u = [...roles]; u.splice(ui, 1); setRoles(u); setIsNewItem(false); }
    setCurrentRole(customListItems.find((i: Role) => i.id === id));
    userService.getUsersForRole(id).then((u) => setUsersForCurrentRole(u));
    actionService.getAllActions().then((r) => { if (r?.length) setActions(r); });
    actionService.getActionsForRole(id).then((r) => { if (r?.length) setPermissionsList(r); });
  };
  const selectUser = (id: number) => {
    if (!id) return false;
    const ui = users.findIndex((u: User) => u.id === undefined);
    if (ui !== -1) { const u = [...users]; u.splice(ui, 1); setUsers(u); setIsNewItem(false); }
    setCurrentUser(customListItems.find((i: User) => i.id === id));
  };
  const selectVisualization = (sel: Visualization) => {
    if (!sel.id) return false;
    const ui = visualizations.findIndex((v: Visualization) => v.id === undefined);
    if (ui !== -1) { const u = [...visualizations]; u.splice(ui, 1); setVisualizations(u); setIsNewItem(false); }
    visualizationService.getVisualization(sel.visualizationCode).then(
      (r) => { if (r.length > 0) setCurrentVisualization(r[0]); },
      (err) => Notify.error(err)
    );
  };
  const selectDashboard = (sel: Dashboard) => {
    if (!sel.id) return false;
    const ui = dashboards.findIndex((d: Dashboard) => d.id === undefined);
    if (ui !== -1) { const u = [...dashboards]; u.splice(ui, 1); setDashboards(u); setIsNewItem(false); }
    dashboardService.getDashboard(sel.dashboardId, sel.profile).then((resp) => {
      if (!resp?.length) return;
      const d = resp[0];
      if (d.filters && d.data?.length) {
        try { const parsed = JSON.parse(d.filters); d.data[0].filters.visualConfig = JSON.stringify(parsed, null, 2); } catch {
          // d.filters is not valid JSON — leave visualConfig unset
        }
      }
      setCurrentDashboard(d);
    }).catch((err) => Notify.error(err));
  };

  const handlePin = (e: any, id: number, type: string, isPinned: boolean) => {
    Notify.success(!isPinned ? "Item is pinned!" : "Item is unpinned!");
    if (type === ENTITY_TYPE.DASHBOARD) {
      dashboardService.getDashboards().then((r: any) => { if (r.length) { setDashboards(r); setFilteredList(r); } }).catch((e: any) => Notify.error(e));
    } else if (type === ENTITY_TYPE.VISUALIZATION) {
      visualizationService.getAllVisualizations().then((r: any) => { if (r.length) { setFilteredList(r); setVisualizations(r); } }).catch((e: any) => Notify.error(e));
    }
  };

  const dragItem     = React.useRef<any>(null);
  const dragOverItem = React.useRef<any>(null);
  const handleSort   = () => {
    const s = [...updatedList];
    const dragged = s.splice(dragItem.current, 1)[0];
    s.splice(dragOverItem.current, 0, dragged);
    dragItem.current = dragOverItem.current = null;
    setCustomListItems(s); setUpdatedList(s);
  };

  const getSelectHandler = (item: any) => () => selectItem(item);
  const getPinHandler    = (item: any) => (e: any) => handlePin(e, item.id, item.type, item.isPinned);
  const formatDate = (ts: number) =>
    ts > 0 ? moment.unix(ts / 1000).format("DD MMM YYYY") : moment.unix(ts).format("DD MMM YYYY");

  const create = createItem({ setIsNewItem, setEditItem, itemType, addNewVisualization, addNewDashboard, addNewUser, addNewRole });
  const clearSearch = () => setSearchQuery("");
  const clearSearchAndList = () => { setSearchQuery(""); setFilteredList(customListItems); setVisualisationFilteredList(customListItems); };
  // What a list shows instead of rows: loading, a failed load with retry, or an empty list (with how to add the first item unless a search is active).
  const emptyState = (icon: string, message: string, hint: string, searching: boolean) => (
    <div className={styles.emptySpan}>
      <EmptyState
        status={status}
        icon={icon}
        message={message}
        hint={searching ? undefined : hint}
        actionLabel={!searching && showFloatingBtn ? btnText : undefined}
        onAction={create}
        onRetry={onRetry}
      />
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // DASHBOARD
  // ══════════════════════════════════════════════════════════════════════════
  const renderDashboardView = () => {
    return (
      <div className={styles.dashPage}>
        {/* ── Fixed header ── */}
        <div className={styles.dashPageHeader}>
          <div className={styles.toolbar}>
            <div className={styles.searchWrapper}>
              <span className={`material-icons ${styles.searchIcon}`}>search</span>
              <input
                className={styles.searchInput}
                aria-label={t('LIST_VIEW.SEARCH_PLACEHOLDER', { itemTypeLabel: t('NAV.DASHBOARDS') })}
                placeholder={t('LIST_VIEW.SEARCH_PLACEHOLDER', { itemTypeLabel: t('NAV.DASHBOARDS') })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <span className={`material-icons ${styles.clearIcon}`} aria-label={t('LIST_VIEW.CLEAR_SEARCH')} {...activateProps(clearSearch)}>close</span>
              )}
            </div>
            {showFloatingBtn && (
              <button className={styles.addBtn} onClick={create}>
                <span className="material-icons">add</span>{t('LIST_VIEW.NEW_DASHBOARD')}
              </button>
            )}
          </div>
          <div className={styles.sectionLabel}>{t('LIST_VIEW.ALL_DASHBOARDS')}</div>
        </div>

        {/* ── Scrollable cards ── */}
        <div className={styles.dashPageScroll}>
          <div className={styles.cardGrid}>
            {displayedDashboards.map((item: Dashboard, idx: number) => {
              const a = CARD_ACCENTS[idx % CARD_ACCENTS.length];
              const dateStr = item.timestamp ? formatDate(item.timestamp) : "—";
              const isSelected = item.id === currentDashboard.id;
              const tag = getDashboardTag(item);
              return (
                <div key={item.id || idx} className={`${styles.dashCard} ${isSelected ? styles.dashCardActive : ""}`} style={{ "--c-accent": tag === "test" ? "var(--state-3)" : a.accent } as React.CSSProperties} {...activateProps(getSelectHandler(item))}>
                  <div className={styles.accentBar} />
                  <div className={styles.cardBody}>
                    <div className={styles.cardIconWrap} style={{ background: a.iconBg }}>
                      <span className="material-icons" style={{ color: a.iconColor, fontSize: 20 }}>dashboard</span>
                    </div>
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div className={styles.cardName}>{item.title}</div>
                      {tag && <StatusBadge tone={tag} label={t(DASHBOARD_TAG_I18N[tag].label)} hint={t(DASHBOARD_TAG_I18N[tag].hint)} />}
                    </div>
                    <div className={styles.cardDate}>{t('LIST_VIEW.UPDATED_ON', { date: dateStr })}</div>
                  </div>
                  <div className={styles.cardFooter}>
                    <span className={`material-icons ${styles.arrowIcon}`}>arrow_forward</span>
                  </div>
                </div>
              );
            })}
            {displayedDashboards.length === 0 && emptyState('dashboard', searchQuery ? t('LIST_VIEW.NO_DASHBOARDS_MATCH_SEARCH') : t('LIST_VIEW.NO_DASHBOARDS_YET'), t('LIST_VIEW.DASHBOARDS_HINT'), !!searchQuery)}
          </div>

          {displayedDashboards.length > 0 && (
            <>
              <div className={styles.sectionLabel} style={{ marginTop: 24 }}>{t('LIST_VIEW.RECENT_ACTIVITY')}</div>
              <div className={styles.table}>
                <div className={styles.tableHead}><span>{t('LIST_VIEW.NAME_COLUMN')}</span><span>{t('LIST_VIEW.LAST_UPDATED_COLUMN')}</span></div>
                {displayedDashboards.map((item: Dashboard, idx: number) => {
                  const tag = getDashboardTag(item);
                  return (
                    <div key={item.id || idx} className={`${styles.tableRow} ${item.id === currentDashboard.id ? styles.tableRowActive : ""}`} {...activateProps(getSelectHandler(item))}>
                      <div className={styles.rowName}>
                        <span className="material-icons" style={{ fontSize: 15, color: "var(--primary-1)", marginRight: 6 }}>dashboard</span>
                        {item.title}
                        {tag && <span className="ms-2"><StatusBadge tone={tag} label={t(DASHBOARD_TAG_I18N[tag].label)} hint={t(DASHBOARD_TAG_I18N[tag].hint)} /></span>}
                      </div>
                      <div className={styles.rowDate}>{item.timestamp ? formatDate(item.timestamp) : "—"}</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // VISUALIZATION
  // ══════════════════════════════════════════════════════════════════════════
  const renderVisualizationView = () => {
    const displayedViz = searchQuery.trim()
      ? visualisationFilteredList.filter((v: Visualization) =>
          (v.title || "").toLowerCase().includes(searchQuery.toLowerCase()))
      : visualisationFilteredList;

    const vizEmpty = () => {
      const searching = !!searchQuery || activeTypeFilter !== "all";
      return emptyState('bar_chart', searching ? t('LIST_VIEW.NO_VISUALIZATIONS_MATCH_FILTER') : t('LIST_VIEW.NO_VISUALIZATIONS_YET'), t('LIST_VIEW.VISUALIZATIONS_HINT'), searching);
    };
    const filteredViz = activeTypeFilter === "all"
      ? displayedViz
      : displayedViz.filter((v: Visualization) =>
          (v.data?.[0]?.info?.chartType || "").toLowerCase() === activeTypeFilter);

    return (
      <div className={styles.dashPage}>
        {/* ── Fixed header ── */}
        <div className={styles.dashPageHeader}>
          <div className={styles.toolbar}>
            <div className={styles.searchWrapper}>
              <span className="material-icons" style={{ fontSize: 18, color: "var(--text-secondary)", flexShrink: 0 }}>search</span>
              <input
                className={styles.searchInput}
                aria-label={t('LIST_VIEW.SEARCH_PLACEHOLDER', { itemTypeLabel: t('NAV.VISUALIZATIONS') })}
                placeholder={t('LIST_VIEW.SEARCH_PLACEHOLDER', { itemTypeLabel: t('NAV.VISUALIZATIONS') })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <span className={`material-icons ${styles.clearIcon}`} aria-label={t('LIST_VIEW.CLEAR_SEARCH')} {...activateProps(clearSearch)}>close</span>
              )}
            </div>
            {showFloatingBtn && (
              <button className={styles.addBtn} onClick={create}>
                <span className="material-icons">add</span>{t('LIST_VIEW.NEW_VISUALIZATION')}
              </button>
            )}
          </div>

          <div className={styles.chipRow}>
            <div className={`${styles.chip} ${activeTypeFilter === "all" ? styles.chipActive : ""}`} aria-pressed={activeTypeFilter === "all"} {...activateProps(() => setActiveTypeFilter("all"))}>
              {t('LIST_VIEW.ALL')} <span className={styles.chipCount}>{displayedViz.length}</span>
            </div>
            {chartTypes.map((ct) => {
              const cfg = getVizTypeConfig(ct);
              return (
                <div key={ct} className={`${styles.chip} ${activeTypeFilter === ct ? styles.chipActive : ""}`} aria-pressed={activeTypeFilter === ct} {...activateProps(() => setActiveTypeFilter(ct))}>
                  <span className="material-icons" style={{ fontSize: 14 }}>{cfg.icon}</span>
                  {cfg.label}
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div className={styles.sectionLabel} style={{ marginBottom: 0 }}>{t('LIST_VIEW.ALL_VISUALIZATIONS')}</div>
            <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
              <button
                onClick={() => setVizViewMode("tile")}
                aria-pressed={vizViewMode === "tile"}
                style={{ width: 30, height: 30, border: `1px solid ${vizViewMode === "tile" ? "var(--primary-1)" : "var(--border-subtle)"}`, borderRadius: 6, background: vizViewMode === "tile" ? "var(--bg-light-2)" : "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: vizViewMode === "tile" ? "var(--primary-1)" : "var(--text-secondary)" }}
                title={t('LIST_VIEW.TILE_VIEW')}
              >
                <span className="material-icons" style={{ fontSize: 17 }}>grid_view</span>
              </button>
              <button
                onClick={() => setVizViewMode("list")}
                aria-pressed={vizViewMode === "list"}
                style={{ width: 30, height: 30, border: `1px solid ${vizViewMode === "list" ? "var(--primary-1)" : "var(--border-subtle)"}`, borderRadius: 6, background: vizViewMode === "list" ? "var(--bg-light-2)" : "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: vizViewMode === "list" ? "var(--primary-1)" : "var(--text-secondary)" }}
                title={t('LIST_VIEW.LIST_VIEW_LABEL')}
              >
                <span className="material-icons" style={{ fontSize: 17 }}>view_list</span>
              </button>
            </div>
          </div>
        </div>

        {/* ── Scrollable cards ── */}
        <div className={styles.dashPageScroll}>
          {/* Tile view */}
          {vizViewMode === "tile" && (
          <div className={styles.cardGrid}>
            {filteredViz.map((item: Visualization, idx: number) => {
              const chartType  = (item.data?.[0]?.info?.chartType || "").toLowerCase();
              const cfg        = getVizTypeConfig(chartType);
              const accent     = CARD_ACCENTS[cfg.accentIdx % CARD_ACCENTS.length];
              const isSelected = item.id === currentVisualization.id;
              const dateStr    = item.timestamp ? formatDate(item.timestamp) : "—";
              return (
                <div key={item.id || idx} className={`${styles.dashCard} ${isSelected ? styles.dashCardActive : ""}`} style={{ "--c-accent": accent.accent } as React.CSSProperties} {...activateProps(getSelectHandler(item))}>
                  <div className={styles.vizPreview} style={{ background: accent.iconBg }}>
                    <span className="material-icons" style={{ fontSize: 40, color: accent.iconColor, opacity: 0.8 }}>{cfg.icon}</span>
                    <span className={styles.typeBadge} style={{ color: accent.iconColor, background: "var(--white-70)" }}>{cfg.label}</span>
                  </div>
                  <div className={styles.cardBody}>
                    <div className={styles.cardName}>{item.title}</div>
                    <div className={styles.cardDate}>{dateStr}</div>
                  </div>
                  <div className={styles.cardFooter}>
                    <span className="material-icons" style={{ fontSize: 14, color: "var(--text-secondary)" }}>arrow_forward</span>
                  </div>
                </div>
              );
            })}
            {filteredViz.length === 0 && vizEmpty()}
          </div>
          )}

          {/* List view */}
          {vizViewMode === "list" && (
            <div className={styles.vizListTable}>
              <div className={styles.vizListHead}>
                <div className={styles.vizListHeadCell}>{t('LIST_VIEW.VISUALIZATION_COLUMN')}</div>
                <div className={styles.vizListHeadCell}>{t('LIST_VIEW.TYPE_COLUMN')}</div>
                <div className={styles.vizListHeadCell}></div>
              </div>
              <div className={styles.vizListBody}>
                {filteredViz.length === 0 && vizEmpty()}
                {filteredViz.map((item: Visualization, idx: number) => {
                  const chartType  = (item.data?.[0]?.info?.chartType || "").toLowerCase();
                  const cfg        = getVizTypeConfig(chartType);
                  const accent     = CARD_ACCENTS[cfg.accentIdx % CARD_ACCENTS.length];
                  const isSelected = item.id === currentVisualization.id;
                  return (
                    <div key={item.id || idx} className={`${styles.vizListRow} ${isSelected ? styles.vizListRowActive : ""}`} {...activateProps(getSelectHandler(item))}>
                      <div className={styles.vizListCell}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: accent.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <span className="material-icons" style={{ fontSize: 18, color: accent.iconColor }}>{cfg.icon}</span>
                        </div>
                        <span className={styles.vizListName}>{item.title}</span>
                      </div>
                      <div className={styles.vizListCell}>
                        <span className={styles.typeBadge} style={{ color: accent.iconColor, background: accent.iconBg, position: "static", fontSize: 11, padding: "2px 8px", borderRadius: 10 }}>{cfg.label}</span>
                      </div>
                      <div className={styles.vizListCell} style={{ justifyContent: "flex-end" }}>
                        <span className="material-icons" style={{ fontSize: 16, color: "var(--text-secondary)" }}>chevron_right</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // All other entity types — original list layout, unchanged
  // ══════════════════════════════════════════════════════════════════════════
  // Translated plural label for the current itemType — shared by the
  // generic search placeholder below (dashboard/visualization views build
  // their own placeholder inline since their itemType is fixed).
  const itemTypeLabels: Record<string, string> = {
    dashboard: t('NAV.DASHBOARDS'),
    visualization: t('NAV.VISUALIZATIONS'),
    user: t('NAV.USERS'),
    role: t('NAV.ROLES'),
  };

  const renderDefaultView = () => (
    <div className={`${styles.pageWrapper} h-100 col-12 p-4 position-relative`}>
      <div className={styles.toolbar} style={{ marginBottom: 16 }}>
        <div className={styles.searchWrapper}>
          <span className={`material-icons ${styles.searchIcon}`}>search</span>
          <input
            className={styles.searchInput}
            aria-label={t('LIST_VIEW.SEARCH_PLACEHOLDER', { itemTypeLabel: itemTypeLabels[itemType] || itemType })}
            placeholder={t('LIST_VIEW.SEARCH_PLACEHOLDER', { itemTypeLabel: itemTypeLabels[itemType] || itemType })}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              const filtered = customListItems.filter((i: SearchableListItem) => matchesListSearch(i, e.target.value));
              setFilteredList(filtered);
              setVisualisationFilteredList(filtered);
            }}
          />
          {searchQuery && (
            <span className={`material-icons ${styles.clearIcon}`} aria-label={t('LIST_VIEW.CLEAR_SEARCH')} {...activateProps(clearSearchAndList)}>close</span>
          )}
        </div>
        {showFloatingBtn && (
          <button className={styles.addBtn} onClick={create}>
            <span className="material-icons">add</span>{btnText}
          </button>
        )}
      </div>

      <div className={itemType === "user" || itemType === "role" ? styles.userPageArea : `${styles.verticalScroll} ${styles.h90} mb-5`}>
        {itemType === "visualization" && visualisationFilteredList.length === 0 && emptyState('bar_chart', t('EMPTY_STATE.NO_RESULTS'), t('LIST_VIEW.VISUALIZATIONS_HINT'), !!searchQuery)}

        {visualisationFilteredList.length > 0 &&
          visualisationFilteredList.map((item: any) => {
            if (itemType !== "visualization") return null;
            return (
              <div className="mb-3" key={item.id}>
                <ListCard
                  showDrag={false} showPin={true} isPinned={item.isPinned}
                  heading={item.title} subHeading={item.description}
                  timestamp={item.timestamp! > 0 ? moment.unix(item.timestamp! / 1000).format("DD/MM/YYYY") : moment.unix(item.timestamp!).format("DD/MM/YYYY")}
                  clickHandler={getSelectHandler(item)}
                  isSelected={matchPathSelection(itemType, location.pathname.split("/")[1], item.id)}
                  pinHandler={getPinHandler(item)}
                />
              </div>
            );
          })}

        {/* User card grid */}
        {itemType === "user" && (
          <>
            {/* Toolbar row: role and geographic filters + view toggle (bottom-aligned so the toggle lines up with the dropdown buttons) */}
            <div className="d-flex align-items-end justify-content-between gap-3 mb-3">
              <UserFilters users={listItems} value={userFilters} onChange={setUserFilters} />
              {/* View toggle */}
              {itemType === "user" && (
                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                  <button
                    onClick={() => setUserViewMode("list")}
                aria-pressed={userViewMode === "list"}
                    style={{ width: 32, height: 32, border: `1px solid ${userViewMode === "list" ? "var(--primary-1)" : "var(--border-subtle)"}`, borderRadius: 6, background: userViewMode === "list" ? "var(--bg-light-2)" : "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: userViewMode === "list" ? "var(--primary-1)" : "var(--text-secondary)" }}
                    title={t('LIST_VIEW.LIST_VIEW_LABEL')}
                  >
                    <span className="material-icons" style={{ fontSize: 18 }}>view_list</span>
                  </button>
                  <button
                    onClick={() => setUserViewMode("tile")}
                aria-pressed={userViewMode === "tile"}
                    style={{ width: 32, height: 32, border: `1px solid ${userViewMode === "tile" ? "var(--primary-1)" : "var(--border-subtle)"}`, borderRadius: 6, background: userViewMode === "tile" ? "var(--bg-light-2)" : "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: userViewMode === "tile" ? "var(--primary-1)" : "var(--text-secondary)" }}
                    title={t('LIST_VIEW.TILE_VIEW')}
                  >
                    <span className="material-icons" style={{ fontSize: 18 }}>grid_view</span>
                  </button>
                </div>
              )}
            </div>{/* end toolbar row */}

            {(() => {
              const visibleUsers = filteredList.filter((u: any) => {
                return matchesUserFilters(u, userFilters);
              });
              return (
                <>
                  {visibleUsers.length === 0 && emptyState('group', t('USER.NO_USERS'), t('LIST_VIEW.USERS_HINT'), !!searchQuery || hasFilters(userFilters))}
                  {/* Tile view */}
                  {userViewMode === "tile" && (
                    <div className={styles.userTileScroll}>
                    <div className={styles.userCardGrid}>
                      {visibleUsers.map((item: any) => {
                        const initials  = ((item.firstName || "")[0] || "U").toUpperCase();
                        const roleName  = item.roles?.[0]?.name || null;
                        const roleObj2  = item.roles?.[0];
                        const scopeCat  = (roleObj2?.admin === true || roleObj2?.superAdmin === true) ? "admin" : "dashboard";
                        const fullName  = [item.firstName, item.lastName].filter(Boolean).join(" ") || "—";
                        return (
                          <div key={item.id} className={styles.userCard} {...activateProps(getSelectHandler(item))}>
                            <div className={styles.userCardTop}>
                              <div className={`${styles.userAvatar} ${scopeCat === "dashboard" ? styles.userAvatarDash : ""}`}>{initials}</div>
                              {roleName && <span className={`${styles.userRoleBadge} ${scopeCat === "dashboard" ? styles.userRoleBadgeDash : ""}`}>{roleName}</span>}
                            </div>
                            <div className={styles.userCardName}>{fullName}</div>
                            <div className={styles.userCardEmail}>{item.emailId || "—"}</div>
                            <div className={styles.userCardArrow}><span className="material-icons">arrow_forward</span></div>
                          </div>
                        );
                      })}
                    </div>
                    </div>
                  )}

                  {/* List view */}
                  {userViewMode === "list" && (
                <div className={styles.userTable}>
                  {/* Table header */}
                  <div className={styles.userTableHead}>
                    <div className={styles.userTableHeadCell}>{t('LIST_VIEW.USER_COLUMN')}</div>
                    <div className={styles.userTableHeadCell}>{t('LIST_VIEW.EMAIL_COLUMN')}</div>
                    <div className={styles.userTableHeadCell}>{t('LIST_VIEW.ROLE_COLUMN')}</div>
                    <div className={styles.userTableHeadCell}></div>
                  </div>
                  {/* Rows */}
                  <div className={styles.userTableBody}>
                  {visibleUsers.map((item: any) => {
                    const initials   = ((item.firstName || "")[0] || "U").toUpperCase();
                    const roleName   = item.roles?.[0]?.name || "—";
                    const roleObj2   = item.roles?.[0];
                    const scopeCat   = (roleObj2?.admin === true || roleObj2?.superAdmin === true) ? "admin" : "dashboard";
                    const fullName   = [item.firstName, item.lastName].filter(Boolean).join(" ") || "—";
                    return (
                      <div key={item.id} className={styles.userTableRow} {...activateProps(getSelectHandler(item))}>
                        {/* Avatar + name */}
                        <div className={styles.userTableCell}>
                          <div className={`${styles.userListAvatar} ${scopeCat === "dashboard" ? styles.userListAvatarDash : ""}`}>{initials}</div>
                          <span className={styles.userListName}>{fullName}</span>
                        </div>
                        {/* Email */}
                        <div className={styles.userTableCell}>
                          <span className={styles.userListEmail}>{item.emailId || "—"}</span>
                        </div>
                        {/* Role badge */}
                        <div className={styles.userTableCell}>
                          <span className={`${styles.userListRoleBadge} ${scopeCat === "dashboard" ? styles.userListRoleBadgeDash : ""}`}>
                            {roleName}
                          </span>
                        </div>
                        {/* Arrow */}
                        <div className={styles.userTableCell} style={{ justifyContent: "flex-end" }}>
                          <span className="material-icons" style={{ fontSize: 16, color: "var(--text-secondary)" }}>chevron_right</span>
                        </div>
                      </div>
                    );
                  })}
                  </div>{/* end userTableBody */}
                </div>
                  )}{/* end list view */}
                </>
              );
            })()}
          </>
        )}

        {/* Role card grid */}
        {itemType === "role" && (
          <>
            {filteredList.length === 0 && <div className={styles.noData}>{t('ROLE.NO_ROLES')}</div>}
            <div className={styles.userCardGrid}>
              {filteredList.map((item: any) => {
                const permCount = item.actions?.length || 0;
                return (
                  <div key={item.id} className={styles.roleCard} {...activateProps(getSelectHandler(item))}>
                    <div className={styles.roleCardIcon}><span className="material-icons">admin_panel_settings</span></div>
                    <div className={styles.roleCardName}>{item.name || "—"}</div>
                    {item.description && <div className={styles.roleCardDesc}>{item.description}</div>}
                    <div className={styles.roleCardMeta}>
                      {permCount > 0 && <span className={styles.roleCardMetaItem}><span className="material-icons" style={{ fontSize: 11 }}>lock</span>{t('ROLE.PERMISSIONS_COUNT', { count: permCount })}</span>}
                    </div>
                    <div className={styles.userCardArrow}><span className="material-icons">arrow_forward</span></div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {filteredList.length > 0 &&
          filteredList.map((item: any, index: number) => {
            if (itemType === "user" || itemType === "role" || itemType === "visualization") return null;
            return (
              <div className="mb-3" key={item.id} draggable
                onDragStart={() => { dragItem.current = index; }}
                onDragEnter={() => { dragOverItem.current = index; }}
                onDragEnd={handleSort}
                onDragOver={(e) => e.preventDefault()}>
                <ListCard
                  showDrag={false} showPin={true} isPinned={item.isPinned}
                  heading={item.title || item.chartName} subHeading={item.srcType || item.description}
                  timestamp={item.timestamp! > 0 ? moment.unix(item.timestamp! / 1000).format("DD/MM/YYYY") : moment.unix(item.timestamp!).format("DD/MM/YYYY")}
                  clickHandler={getSelectHandler(item)}
                  isSelected={matchPathSelection(itemType, location.pathname.split("/")[1], item.id)}
                  pinHandler={getPinHandler(item)}
                />
              </div>
            );
          })}
      </div>
    </div>
  );

  if (itemType === ENTITY_TYPE.DASHBOARD) return renderDashboardView();
  if (itemType === ENTITY_TYPE.VISUALIZATION) return renderVisualizationView();
  return renderDefaultView();
};